import { Injectable } from "@angular/core";
import { MsalBroadcastService, MsalService } from "@azure/msal-angular";
import { AccountInfo, InteractionStatus } from "@azure/msal-browser";
import { BehaviorSubject } from "rxjs";
import { filter, map } from "rxjs/operators";

@Injectable({
    providedIn: "root",
})
export class AuthService {
    // Observable holding current account info
    private currentAccountSubject = new BehaviorSubject<AccountInfo | null>(
        null
    );
    currentAccount$ = this.currentAccountSubject.asObservable();
    private user: AccountInfo = null;

    get userInfo() {
        return this.currentAccountSubject.asObservable();
    }

    get isUserLoggedIn() {
        return this.userInfo.pipe(
            map(
                (user) => user != null && !this.validateToken(user.idTokenClaims)
            )
        );
    }

    validateToken(idTokenClaims: any): boolean {
        if (!idTokenClaims || !idTokenClaims.exp) {
            return false;
        }

        const currentTime = Math.floor(Date.now() / 1000); // Current time in seconds
        return idTokenClaims.exp > currentTime;
    }

    constructor(
        private msalService: MsalService,
        private msalBroadcastService: MsalBroadcastService
    ) {
        // On service init, load any cached accounts
        this.loadAccount();

        // Listen for interaction status changes (login, logout, token acquisition)
        this.msalBroadcastService.inProgress$
            .pipe(filter((status) => status === InteractionStatus.None))
            .subscribe(() => {
                this.loadAccount();
            });
    }

    private loadAccount() {
        const accounts = this.msalService.instance.getAllAccounts();

        if (accounts.length > 0) {
            // Choose the first account (customize if needed)
            const activeAccount = accounts[0];
            this.msalService.instance.setActiveAccount(activeAccount);
            this.currentAccountSubject.next(activeAccount);
            console.log("Active account set:", activeAccount);
        } else {
            this.msalService.instance.setActiveAccount(null);
            this.currentAccountSubject.next(null);
            console.log("No active account found");
        }
    }

    // Helper method to get current account synchronously
    getCurrentAccount(): AccountInfo | null {
        return this.currentAccountSubject.value;
    }

    // Helper to check if user is logged in
    isLoggedIn(): boolean {
        return this.getCurrentAccount() != null;
    }

    get isUserAuthorized$() {
        return this.userInfo.pipe(map(this.isUserAuthorizedInternal));
    }

    private isUserAuthorizedInternal(account: AccountInfo) {
        return account?.idTokenClaims?.roles.length > 0;
    }

    isUserAuthorized(): boolean {
        return this.isUserAuthorizedInternal(this.user);
    }

}
